import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Alert, Anchor, Grid, Group, Paper as MantinePaper, Text, Title } from "@mantine/core";
import { observer } from "mobx-react";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import { useLayoutEffect, useRef, useState } from "react";
import { MdExpandMore } from "react-icons/md";
import styles from "./styles/Episode.module.css";
import { Breadcrumbs } from "../Breadcrumbs";
import { PlayButton } from "../player/PlayButton";
import { DownloadButton } from "../DownloadButton";
import { ShareButton } from "../ShareButton";
import { AddToPlaylistButton } from "../playlist/AddToPlaylistButton";
import { RemoveFromPlaylistButton } from "../playlist/RemoveFromPlaylistButton";
import { Paper } from "../Paper";
import { createDurationMin, filterText, filterTitle, getEpisodeImage, getFirstCategory, timeAgo } from "@/core/helpers";
import { useStores } from "@/hooks/useStores";
import { linkCategories } from "@/core/autolinking";
import { useEpisodeAds } from "@/hooks/useEpisodeAds";

const SQUARE_SIZE = 360;

interface StandaloneEpisodeProps {
    episode: Episode;
    podcast: Podcast;
}

export const StandaloneEpisode = observer(({ episode, podcast }: StandaloneEpisodeProps) => {
    const ad = useEpisodeAds(podcast.categories);
    const { podcastStore, playlistStore, categoryStore } = useStores();
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [useToggle, setUseToggle] = useState<boolean>(false);
    const [imageLoaded, setImageLoaded] = useState<boolean>(false);
    const isVideo = episode.sound.endsWith(".mp4");
    const descriptionRef = useRef<HTMLDivElement>(null);
    const maxHeight = 260;

    const isPreloading = useRef<boolean>(false);

    const isFotboll = podcast?.categories.some((c) => c.slug === "fotboll");

    if (!isPreloading.current) {
        isPreloading.current = true;
        const src = getEpisodeImage(episode, podcast);
        const img = new Image();
        img.onload = () => {
            setTimeout(() => {
                setImageLoaded(true);
            }, 0);
        };
        img.src = src;
    }

    useLayoutEffect(() => {
        if (descriptionRef.current) {
            const height = descriptionRef.current.offsetHeight;
            if (height > maxHeight) {
                setUseToggle(true);
            }
        }
    }, []);

    const contentCls = `${styles.content}${useToggle && !isExpanded ? ` ${styles.short}` : ""}`;

    return (
        <>
            <Helmet>
                <link rel="preload" as="image" href={getEpisodeImage(episode, podcast)} />
                <script type={"application/ld+json"}>{`
                        {
                            "@context": "https://schema.org",
                            "@type": "PodcastEpisode",
                            "url": "https://hittapodd.se/podd/${podcast.slug}/${episode.slug}",
                            "name": "${episode.title}", 
                            "datePublished": "${episode.published.substring(0, 10)}",
                            "timeRequired": "PT${createDurationMin(episode.length)}M",
                            "description": "${episode.snippet}",
                            "associatedMedia": {
                                "@type": "MediaObject",
                                "contentUrl": "${episode.sound}"
                            },
                            "partOfSeries": {
                                "@type": "PodcastSeries",
                                "name": "${podcast.name}",
                                "url": "https://hittapodd.se/podd/${podcast.slug}"
                             }
                        }
                    `}</script>
            </Helmet>

            <Breadcrumbs
                items={[
                    {
                        label: "Kategorier",
                        link: "/kategori",
                    },
                    {
                        ...getFirstCategory(podcast.categories),
                    },
                    {
                        label: podcast.name,
                        link: `/podd/${podcast.slug}`,
                    },
                    {
                        active: true,
                        label: episode.title,
                        link: `/podd/${podcast.slug}/${episode.slug}`,
                    },
                ]}
            />
            <Title
                mb="md"
                order={1}
                dangerouslySetInnerHTML={{
                    __html: filterTitle(episode.title),
                }}
            />
            <Title mb="md" order={3}>
                <Anchor component={Link} to={"/podd/" + podcast.slug} underline="hover">
                    {podcast.name}
                </Anchor>
            </Title>
            <Paper>
                <Grid>
                    <Grid.Col span={{ base: 12, md: 4 }}>
                        {!isVideo && imageLoaded && (
                            <>
                                <div className={styles.img}>
                                    <img
                                        loading={"eager"}
                                        alt={podcast.name}
                                        src={getEpisodeImage(episode, podcast)}
                                        width={SQUARE_SIZE}
                                        height={SQUARE_SIZE}
                                        title={podcast.name + " - " + episode.title}
                                    />
                                    <div className={styles.play}>
                                        <PlayButton
                                            size={"large"}
                                            key={episode.guid}
                                            podcast={podcast}
                                            episode={episode}
                                        />
                                    </div>
                                </div>
                                <Alert color="blue" mt="md">
                                    Alla bilder och innehåll är syndikerat från {podcast.name}{" "}
                                    <a target={"_blank"} rel={"noopener noreferrer"} href={podcast.feed}>
                                        RSS-feed
                                    </a>{" "}
                                    som ägs och drivs av {podcast.author.name}.{" "}
                                </Alert>
                            </>
                        )}
                        {!imageLoaded && (
                            <div className={styles.img}>
                                <div className={styles.placeholder}>&nbsp;</div>
                            </div>
                        )}
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 8 }}>
                        {episode.subjects && episode.subjects.length > 0 && (
                            <Text className={styles.focus} mb="xs" size="sm">
                                <Text fw={700} span>
                                    Fokus:
                                </Text>{" "}
                                {episode.subjects.map((subject: Subject) => {
                                    return (
                                        <Anchor
                                            component={Link}
                                            key={`subject-${subject.slug}`}
                                            to={`/fokus/${subject.slug}`}>
                                            {subject.name}
                                        </Anchor>
                                    );
                                })}
                            </Text>
                        )}

                        <Text
                            c="dimmed"
                            className={styles.time}
                            component="time"
                            dateTime={episode.published}
                            size="sm">
                            Publicerad: {episode.published.substring(0, 10)} - {timeAgo(episode.published)} - Längd:{" "}
                            {createDurationMin(episode.length)} minuter
                        </Text>

                        {episode.content.length > 0 && (
                            <div ref={descriptionRef}>
                                <div
                                    className={contentCls}
                                    dangerouslySetInnerHTML={{
                                        __html: linkCategories(filterText(episode.content), categoryStore.categoryMap),
                                    }}
                                />
                                {useToggle && (
                                    <Text className={styles.toggle}>
                                        <Anchor
                                            onClick={(event) => {
                                                event.preventDefault();
                                                setIsExpanded(!isExpanded);
                                            }}
                                            href={"#toggle"}
                                            underline="never">
                                            {isExpanded ? (
                                                <>
                                                    <MdExpandMore className={styles.flip} /> Visa mindre
                                                </>
                                            ) : (
                                                <>
                                                    <MdExpandMore /> Visa mer
                                                </>
                                            )}
                                        </Anchor>
                                    </Text>
                                )}
                            </div>
                        )}
                        <Group className={styles.actions} gap="xs">
                            {!isVideo && <PlayButton episode={episode} podcast={podcast} labels={["Spela", "Pausa"]} />}
                            {!playlistStore.inList(episode) && (
                                <AddToPlaylistButton episode={episode} podcast={podcast} />
                            )}
                            {playlistStore.inList(episode) && (
                                <RemoveFromPlaylistButton episode={episode} podcast={podcast} />
                            )}
                            <DownloadButton episode={episode} />
                            <ShareButton episode={episode} podcast={podcast} />
                        </Group>

                        {ad ? (
                            <MantinePaper
                                className={styles["episode-ad"]}
                                component="a"
                                p="md"
                                radius="xl"
                                target={"_blank"}
                                rel={"noreferrer noopener"}
                                href={ad.url}
                                withBorder>
                                <Title mb="sm" order={5}>
                                    {ad.title}
                                </Title>
                                <Text mb="sm" size="sm">
                                    {ad.description}
                                </Text>
                                <img width={120} src={ad.image} alt={ad.title} />
                            </MantinePaper>
                        ) : null}
                        {isFotboll ? (
                            <Alert className={styles.football} color="teal" radius="lg">
                                Statistik om Allsvenskan, Superettan mfl hittar du på{" "}
                                <Anchor
                                    href={
                                        "https://fotbollsfeber.se?utm_source=hittapodd&utm_medium=link&utm_campaign=episode"
                                    }
                                    rel={"noopener noreferrer dofollow"}
                                    target={"_blank"}>
                                    Fotbollsfeber.se
                                </Anchor>
                            </Alert>
                        ) : null}
                    </Grid.Col>
                </Grid>

                {isVideo && (
                    <video controls className={styles.video}>
                        <source src={episode.sound} type="video/mp4" />
                    </video>
                )}
            </Paper>

            <Group className={styles.paging} justify="space-between" wrap="nowrap">
                {podcastStore.previous && (
                    <Anchor
                        className={styles.pagingButton}
                        component={Link}
                        title={podcastStore.previous.title}
                        to={`/podd/${podcast.slug}/${podcastStore.previous.slug}`}
                        underline="never">
                        <span className={styles.pagingCopy}>
                            <span className={styles.pagingLabel}>Föregående</span>
                            <span className={styles.pagingTitle}>
                                <GrFormPrevious />
                                <span className={styles.truncate}>{filterTitle(podcastStore.previous.title)}</span>
                            </span>
                        </span>
                    </Anchor>
                )}
                {podcastStore.next && (
                    <Anchor
                        className={styles.pagingButton}
                        component={Link}
                        title={podcastStore.next.title}
                        to={`/podd/${podcast.slug}/${podcastStore.next.slug}`}
                        underline="never">
                        <span className={styles.pagingCopy}>
                            <span className={styles.pagingLabel}>Nästa</span>
                            <span className={styles.pagingTitle}>
                                <span className={styles.truncate}>{filterTitle(podcastStore.next.title)}</span>
                                <GrFormNext />
                            </span>
                        </span>
                    </Anchor>
                )}
            </Group>
        </>
    );
});
